/**
 * Saved filter views — the persistence port behind the filter bar's "Saved filters" popover.
 *
 * A view is a named snapshot of the bar's conditions, join mode and whatever host state the
 * surface chooses to keep (`columns`, opaque to the store). The store is a port: what ships is a
 * localStorage adapter keyed per surface, so a leader's Item Scorer Summary views never appear
 * on the Responses view and vice-versa. The mockup's private / shared / everyone library is an
 * API-backed adapter behind the same interface — swap the adapter, keep the bar.
 */

import {
  FilterCondition,
  FilterFieldDef,
  FilterJoinMode,
  fieldById,
  normalizeIdList,
} from './recordFilterEngine';

export interface SavedFilterView {
  id: string;
  name: string;
  savedAt: string;
  /** Set when the view was overwritten by saving under the same name. */
  updatedAt?: string;
  /** 1 on first save; +1 per overwrite. Views saved before versioning read as 1. */
  version?: number;
  /** Surface key the view was saved from (informational — the store already scopes by it). */
  surface?: string;
  joinMode: FilterJoinMode;
  conditions: FilterCondition[];
  /** Host-owned extra state (column-group toggle ids on scorer stats). Opaque here. */
  columns: string[];
}

export interface FilterPresetStore {
  load(): SavedFilterView[];
  save(views: SavedFilterView[]): void;
}

export const filterPresetStorageKey = (surface: string): string =>
  `vea.scoringLeader.${surface}.filterViews.v1`;

/** Browser-local store. Failures (private mode, quota, corrupt JSON) degrade to "no views". */
export class LocalStorageFilterPresetStore implements FilterPresetStore {
  readonly key: string;

  constructor (surfaceOrKey: string, private readonly storage: Storage | null = defaultStorage()) {
    this.key = surfaceOrKey.indexOf('.') === -1 ? filterPresetStorageKey(surfaceOrKey) : surfaceOrKey;
  }

  load (): SavedFilterView[] {
    try {
      const raw = this.storage ? this.storage.getItem(this.key) : null;
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(isViewShaped) : [];
    } catch {
      return [];
    }
  }

  save (views: SavedFilterView[]): void {
    try {
      if (this.storage) this.storage.setItem(this.key, JSON.stringify(views || []));
    } catch {
      // Private browsing / quota — the views are a convenience, not a requirement.
    }
  }
}

/** In-memory store for specs and for surfaces that must not persist. */
export class MemoryFilterPresetStore implements FilterPresetStore {
  private views: SavedFilterView[] = [];
  load (): SavedFilterView[] { return this.views.map(v => ({...v})); }
  save (views: SavedFilterView[]): void { this.views = (views || []).map(v => ({...v})); }
}

const defaultStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
};

const isViewShaped = (value: any): value is SavedFilterView =>
  !!value && typeof value === 'object' && typeof value.name === 'string' && Array.isArray(value.conditions);

export const newFilterViewId = (): string =>
  `view_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export interface SavedFilterViewDraft {
  name: string;
  joinMode: FilterJoinMode;
  conditions: FilterCondition[];
  columns?: string[];
  surface?: string;
}

export interface UpsertResult {
  views: SavedFilterView[];
  view: SavedFilterView;
  /** True when an existing view of the same name was replaced. */
  replaced: boolean;
}

/**
 * Save a draft. A view with the same name (case-insensitive, trimmed) is **replaced** in place
 * and its version bumped rather than duplicated — "version on save", the lite form of the
 * mockup's history. Conditions are snapshotted so later chip edits do not rewrite the view.
 */
export const upsertSavedFilterView = (
  views: SavedFilterView[],
  draft: SavedFilterViewDraft,
  now: Date = new Date(),
): UpsertResult => {
  const name = (draft.name || '').trim();
  const key = name.toLowerCase();
  const existing = (views || []).find(v => (v.name || '').trim().toLowerCase() === key);
  const stamp = now.toISOString();
  const snapshot = (draft.conditions || []).map(c => ({...c}));

  if (existing) {
    const view: SavedFilterView = {
      ...existing,
      name,
      updatedAt: stamp,
      version: (existing.version || 1) + 1,
      joinMode: draft.joinMode,
      conditions: snapshot,
      columns: [...(draft.columns || [])],
      surface: draft.surface || existing.surface,
    };
    return { views: views.map(v => v === existing ? view : v), view, replaced: true };
  }

  const view: SavedFilterView = {
    id: newFilterViewId(),
    name,
    savedAt: stamp,
    version: 1,
    surface: draft.surface,
    joinMode: draft.joinMode,
    conditions: snapshot,
    columns: [...(draft.columns || [])],
  };
  return { views: [...(views || []), view], view, replaced: false };
};

export const removeSavedFilterView = (views: SavedFilterView[], id: string): SavedFilterView[] =>
  (views || []).filter(v => v.id !== id);

export interface AppliedFilterView {
  conditions: FilterCondition[];
  /** Item ids in the view that do not exist in the current scope (e.g. this marking window). */
  droppedItemIds: number[];
  /** Field ids the view referenced that this surface's catalogue no longer has. */
  droppedFieldIds: string[];
}

/**
 * Rehydrate a view against a catalogue and the item ids currently in scope. Conditions on
 * unknown fields are dropped; item conditions lose out-of-scope ids and are dropped when
 * nothing is left, rather than kept as a condition that can never match. Every drop is
 * reported so the bar can say what it left out instead of silently narrowing.
 */
export const resolveSavedFilterView = <TRow, TCtx>(
  view: SavedFilterView,
  fields: FilterFieldDef<TRow, TCtx>[],
  availableItemIds: number[] | null = null,
): AppliedFilterView => {
  const available = availableItemIds == null ? null : new Set(normalizeIdList(availableItemIds));
  const droppedItems = new Set<number>();
  const droppedFields = new Set<string>();
  const conditions: FilterCondition[] = [];

  for (const saved of view.conditions || []) {
    const field = fieldById(fields, saved.field);
    if (!field) {
      if (saved.field) droppedFields.add(saved.field);
      continue;
    }
    const condition: FilterCondition = {...saved};
    if (field.type === 'items' && available) {
      const ids = normalizeIdList(condition.value);
      ids.filter(id => !available.has(id)).forEach(id => droppedItems.add(id));
      condition.value = ids.filter(id => available.has(id));
      if (!condition.value.length) continue;
    }
    conditions.push(condition);
  }

  return {
    conditions,
    droppedItemIds: Array.from(droppedItems),
    droppedFieldIds: Array.from(droppedFields),
  };
};

/** One-line notice for the bar after applying a view. */
export const describeAppliedView = (view: SavedFilterView, applied: AppliedFilterView): string => {
  const notes: string[] = [];
  if (applied.droppedItemIds.length) {
    notes.push(`${applied.droppedItemIds.length} item(s) in it are not in this scoring window and were left out`);
  }
  if (applied.droppedFieldIds.length) {
    notes.push(`${applied.droppedFieldIds.length} filter(s) on fields this view no longer has were left out`);
  }
  return notes.length ? `Applied “${view.name}” — ${notes.join('; ')}.` : `Applied “${view.name}”.`;
};
